import { useTranslation } from '@e-kyc/translations';
import {
  hasNameLetter,
  isValidNameCharset,
  NAME_ALLOWED_CHARACTERS,
  THAI_TITLES,
} from '@shared/utils';
import { z } from 'zod';

const MAX_NAME_LENGTH = 256;

export const useIdCardConfirmationFormSchema = () => {
  const { t } = useTranslation();

  return z.object({
    idCardNumber: z.string().optional(),
    title: z.enum(THAI_TITLES, {
      message: t('idCardDetails.form.error.pleaseSelect', {
        label: t('idCardDetails.form.title.label'),
      }),
    }),
    // All three name fields share one rule and differ only in required-ness. A
    // punctuation-only value such as '-' is rejected — the citizen-card OCR sends it to mean
    // "no name given". Middle name additionally accepts '' since it is optional. The charset
    // and letter rules are checked separately because they have different error copy. All
    // checks run after .trim(), so '  -  ' is caught too.
    firstName: z
      .string()
      .trim()
      .min(1, {
        message: t('idCardDetails.form.error.pleaseFill', {
          label: t('idCardDetails.form.firstName.label'),
        }),
      })
      .max(MAX_NAME_LENGTH, {
        message: t('idCardDetails.form.error.maxLength', {
          label: t('idCardDetails.form.firstName.label'),
          max: MAX_NAME_LENGTH,
        }),
      })
      .regex(NAME_ALLOWED_CHARACTERS, {
        message: t('idCardDetails.form.error.invalidNameCharacters'),
      })
      .refine(hasNameLetter, {
        message: t('idCardDetails.form.error.nameMustContainLetter'),
      }),
    middleName: z
      .string()
      .trim()
      .max(MAX_NAME_LENGTH, {
        message: t('idCardDetails.form.error.maxLength', {
          label: t('idCardDetails.form.middleName.label'),
          max: MAX_NAME_LENGTH,
        }),
      })
      .refine((value) => value === '' || isValidNameCharset(value), {
        message: t('idCardDetails.form.error.invalidNameCharacters'),
      })
      .refine((value) => value === '' || hasNameLetter(value), {
        message: t('idCardDetails.form.error.nameMustContainLetter'),
      })
      .optional(),
    lastName: z
      .string()
      .trim()
      .min(1, {
        message: t('idCardDetails.form.error.pleaseFill', {
          label: t('idCardDetails.form.lastName.label'),
        }),
      })
      .max(MAX_NAME_LENGTH, {
        message: t('idCardDetails.form.error.maxLength', {
          label: t('idCardDetails.form.lastName.label'),
          max: MAX_NAME_LENGTH,
        }),
      })
      .regex(NAME_ALLOWED_CHARACTERS, {
        message: t('idCardDetails.form.error.invalidNameCharacters'),
      })
      .refine(hasNameLetter, {
        message: t('idCardDetails.form.error.nameMustContainLetter'),
      }),
    dateOfBirth: z
      .object({
        date: z.preprocess((v) => v ?? '-', z.string()),
        month: z.preprocess((v) => v ?? '-', z.string()),
        year: z.preprocess((v) => v ?? '-', z.string()),
      })
      .superRefine((data, ctx) => {
        const digitRegex = /^\d+$/;
        const fields = [
          { value: data.date, path: 'date' },
          { value: data.month, path: 'month' },
          { value: data.year, path: 'year' },
        ] as const;

        const hasError = fields.some((f) => !f.value || !digitRegex.test(f.value));

        if (hasError) {
          for (const field of fields) {
            if (!field.value || !digitRegex.test(field.value)) {
              ctx.addIssue({
                code: z.ZodIssueCode.custom,
                message: '',
                path: [field.path],
              });
            }
          }
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: t('idCardDetails.form.error.dateOfBirth'),
          });
        }
      }),
    cardExpirationDate: z
      .object({
        date: z.preprocess((v) => v ?? '-', z.string()),
        month: z.preprocess((v) => v ?? '-', z.string()),
        year: z.preprocess((v) => v ?? '-', z.string()),
      })
      .superRefine((data, ctx) => {
        const { date, month, year } = data;
        const fields = [date, month, year];
        const emptyFields = fields.filter((field) => field === '-' || field === null).length;

        if (emptyFields > 0 && emptyFields < fields.length) {
          const errorMessage = t('idCardDetails.form.error.cardExpirationDate');

          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '',
            path: ['date'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '',
            path: ['month'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: '',
            path: ['year'],
          });
          ctx.addIssue({
            code: z.ZodIssueCode.custom,
            message: errorMessage,
          });
        }
      }),

    laserNumber: z.string().min(1, {
      message: t('idCardDetails.form.error.pleaseFill', {
        label: t('idCardDetails.form.laserNo.label'),
      }),
    }),
  });
};

export type IdCardConfirmationFormType = z.infer<
  ReturnType<typeof useIdCardConfirmationFormSchema>
>;
